import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FiCheck, FiX } from "react-icons/fi";
import { Button } from "../../ui/Button";
import * as D from "./Farms.desktop.styled";

const FarmFilterDrawerDesktop = ({
  open,
  pendingFilters,
  onRegionSelect,
  onToggleProductType,
  onOrganicToggle,
  onDeliverySelect,
  onApply,
  onClearPending,
  onClose,
  regionOptions,
  productTypeOptions,
  deliveryWindowOptions,
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <D.DrawerOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            aria-hidden="true"
          />
          <D.DrawerPanel
            as={motion.aside}
            role="dialog"
            aria-modal="true"
            aria-label={t("farms.filters.title", {
              defaultValue: "Filter farms",
            })}
            initial={{ x: reduceMotion ? 0 : "100%" }}
            animate={{ x: 0 }}
            exit={{ x: reduceMotion ? 0 : "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
          >
            <D.DrawerHeader>
              <div>
                <D.DrawerTitle>
                  {t("farms.filters.title", { defaultValue: "Filter farms" })}
                </D.DrawerTitle>
                <D.DrawerSubtitle>
                  {t("farms.filters.subtitle", {
                    defaultValue:
                      "Narrow results by region, products, and delivery preferences.",
                  })}
                </D.DrawerSubtitle>
              </div>
              <D.DrawerClose
                type="button"
                onClick={onClose}
                aria-label={t("farms.filters.close", {
                  defaultValue: "Close filters",
                })}
              >
                <FiX aria-hidden="true" />
              </D.DrawerClose>
            </D.DrawerHeader>

            <D.DrawerBody>
              <D.DrawerFieldset>
                <D.DrawerLegend>
                  {t("farms.filters.region.label", { defaultValue: "Region" })}
                </D.DrawerLegend>
                <D.DrawerChipGroup>
                  {regionOptions.map((option) => (
                    <D.QuickChip
                      key={option.value}
                      type="button"
                      $active={pendingFilters.region === option.value}
                      aria-pressed={pendingFilters.region === option.value}
                      onClick={() => onRegionSelect(option.value)}
                    >
                      {option.label}
                    </D.QuickChip>
                  ))}
                </D.DrawerChipGroup>
              </D.DrawerFieldset>

              <D.DrawerFieldset>
                <D.DrawerLegend>
                  {t("farms.filters.products.label", {
                    defaultValue: "Product types",
                  })}
                  <D.DrawerLegendHint>
                    {t("farms.filters.multi", {
                      defaultValue: "(pick any)",
                    })}
                  </D.DrawerLegendHint>
                </D.DrawerLegend>
                <D.DrawerChipGroup>
                  {productTypeOptions.map((option) => (
                    <D.QuickChip
                      key={option.value}
                      type="button"
                      $active={pendingFilters.productTypes.includes(option.value)}
                      aria-pressed={pendingFilters.productTypes.includes(
                        option.value
                      )}
                      onClick={() => onToggleProductType(option.value)}
                    >
                      {pendingFilters.productTypes.includes(option.value) ? (
                        <FiCheck size={14} aria-hidden="true" />
                      ) : null}
                      {option.label}
                    </D.QuickChip>
                  ))}
                </D.DrawerChipGroup>
              </D.DrawerFieldset>

              <D.DrawerFieldset>
                <D.DrawerLegend>
                  {t("farms.filters.organic.label", {
                    defaultValue: "Organic",
                  })}
                </D.DrawerLegend>
                <D.SwitchRow>
                  <D.SwitchLabel>
                    <strong>
                      {t("farms.filters.organic.copy", {
                        defaultValue: "Show certified organic farms only",
                      })}
                    </strong>
                    <span>
                      {t("farms.filters.organic.hint", {
                        defaultValue:
                          "Verified organic partners with traceable sourcing.",
                      })}
                    </span>
                  </D.SwitchLabel>
                  <D.Switch
                    type="checkbox"
                    checked={pendingFilters.organicOnly}
                    onChange={(event) => onOrganicToggle(event.target.checked)}
                  />
                </D.SwitchRow>
              </D.DrawerFieldset>

              <D.DrawerFieldset>
                <D.DrawerLegend>
                  {t("farms.filters.delivery.label", {
                    defaultValue: "Delivery window",
                  })}
                </D.DrawerLegend>
                <D.DrawerChipGroup>
                  {deliveryWindowOptions.map((option) => (
                    <D.QuickChip
                      key={option.value}
                      type="button"
                      $active={pendingFilters.deliveryWindow === option.value}
                      aria-pressed={
                        pendingFilters.deliveryWindow === option.value
                      }
                      onClick={() => onDeliverySelect(option.value)}
                    >
                      {option.label}
                    </D.QuickChip>
                  ))}
                </D.DrawerChipGroup>
              </D.DrawerFieldset>
            </D.DrawerBody>

            <D.DrawerFooter>
              <Button type="button" variant="outline" onClick={onClearPending}>
                {t("farms.filters.reset", { defaultValue: "Reset" })}
              </Button>
              <Button type="button" variant="primary" onClick={onApply}>
                {t("farms.filters.apply", { defaultValue: "Apply filters" })}
              </Button>
            </D.DrawerFooter>
          </D.DrawerPanel>
        </>
      ) : null}
    </AnimatePresence>
  );
};

export default FarmFilterDrawerDesktop;
